import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../api';
import { disconnectSocket } from '../socket';

const AuthContext = createContext(null);

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const WARN_BEFORE_MS     = 60 * 1000;      // warn 1 minute before
const CHECK_INTERVAL_MS  = 5  * 1000;      // check every 5 seconds

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('marvy_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const [warnLogout, setWarnLogout] = useState(false);

  const timeoutMs    = useRef(DEFAULT_TIMEOUT_MS);
  const lastActivity = useRef(Date.now());
  const intervalRef  = useRef(null);

  const logout = useCallback(() => {
    api.logout().catch(() => {});
    api.oidcLogout();
    disconnectSocket();
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setWarnLogout(false);
    localStorage.removeItem('marvy_token');
    localStorage.removeItem('marvy_user');
    setUser(null);
  }, []);

  // Reset the last-activity timestamp on any user interaction
  const resetActivity = useCallback(() => {
    lastActivity.current = Date.now();
    setWarnLogout(false);
  }, []);

  const fetchTimeout = useCallback(async () => {
    try {
      const data = await api.getSessionTimeout();
      const mins = parseInt(data.minutes) || 5;
      timeoutMs.current = mins * 60 * 1000;
    } catch { /* keep default */ }
  }, []);

  useEffect(() => {
    if (!user) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      setWarnLogout(false);
      return;
    }

    // Fetch configured timeout then start watching
    fetchTimeout().then(() => {
      lastActivity.current = Date.now();

      // Poll every 5 seconds to check idle time
      intervalRef.current = setInterval(() => {
        const idle = Date.now() - lastActivity.current;
        const ms   = timeoutMs.current;

        if (idle >= ms) {
          logout();
        } else if (ms - idle <= WARN_BEFORE_MS) {
          setWarnLogout(true);
        } else {
          setWarnLogout(false);
        }
      }, CHECK_INTERVAL_MS);
    });

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetActivity, { passive: true }));

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      events.forEach(e => window.removeEventListener(e, resetActivity));
    };
  }, [user, fetchTimeout, logout, resetActivity]);

  const login = useCallback(async (email, password) => {
    try {
      const { token, user: userData } = await api.login(email, password);
      localStorage.setItem('marvy_token', token);
      localStorage.setItem('marvy_user',  JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Invalid email or password.' };
    }
  }, []);

  const loginWithPasskey = useCallback(async () => {
    try {
      const { token, user: userData } = await api.loginWithPasskey();
      localStorage.setItem('marvy_token', token);
      localStorage.setItem('marvy_user',  JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message || 'Face ID sign-in failed.' };
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.me();
      localStorage.setItem('marvy_user', JSON.stringify(userData));
      setUser(userData);
    } catch { logout(); }
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, login, loginWithPasskey, logout, refreshUser }}>
      {children}
      {warnLogout && user && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 bg-amber-600 text-white px-5 py-3 rounded-xl shadow-2xl text-sm font-medium">
          <span>You'll be logged out in 1 minute due to inactivity.</span>
          <button
            onClick={resetActivity}
            className="px-3 py-1 bg-white text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-50 transition-colors"
          >
            Stay logged in
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
